import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

type AccountRow = {
  id: string
  full_name: string | null
  email: string | null
  phone: string | null
  role: string | null
  address_line1: string | null
  city: string | null
  zip_code: string | null
  country: string | null
  partner_id: string | null
  partner_role: string | null
  created_at: string
  updated_at: string
  order_count: number
}

type SortKey = 'full_name' | 'email' | 'created_at' | 'order_count' | 'role'
type SortDir = 'asc' | 'desc'

const AdminAccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<'ALL' | string>('ALL')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const loadAccounts = async () => {
    setLoading(true)
    setError(null)

    // Fetch profiles
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone, role, address_line1, city, zip_code, country, partner_id, partner_role, created_at, updated_at')
      .order('created_at', { ascending: false })

    if (profilesError) {
      console.error(profilesError)
      setError('Erreur lors du chargement des comptes.')
      setLoading(false)
      return
    }

    // Fetch order counts per user
    const { data: orderCounts, error: ordersError } = await supabase
      .from('orders')
      .select('user_id')

    if (ordersError) {
      console.error(ordersError)
      setError('Erreur lors du chargement du nombre de réservations.')
      setLoading(false)
      return
    }

    // Build count map
    const countMap: Record<string, number> = {}
    for (const o of orderCounts ?? []) {
      countMap[o.user_id] = (countMap[o.user_id] ?? 0) + 1
    }

    const rows: AccountRow[] = (profiles ?? []).map((p: any) => ({
      id: p.id,
      full_name: p.full_name,
      email: p.email,
      phone: p.phone,
      role: p.role,
      address_line1: p.address_line1,
      city: p.city,
      zip_code: p.zip_code,
      country: p.country,
      partner_id: p.partner_id,
      partner_role: p.partner_role,
      created_at: p.created_at,
      updated_at: p.updated_at,
      order_count: countMap[p.id] ?? 0,
    }))

    setAccounts(rows)
    setLoading(false)
  }

  useEffect(() => {
    loadAccounts()
  }, [])

  const handleDelete = async (account: AccountRow) => {
    const displayName = account.full_name || account.email || account.id
    const confirmed = window.confirm(
      `⚠️ Supprimer le compte de ${displayName} ?\n\n` +
      'Cette action est irréversible. Le profil sera anonymisé et le compte auth supprimé.'
    )
    if (!confirmed) return

    setDeletingId(account.id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        alert('Erreur : session expirée, reconnectez-vous.')
        return
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/admin-delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: account.id }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la suppression')
      }

      if (data.ok) {
        await loadAccounts()
      }
    } catch (err) {
      console.error('Erreur suppression:', err)
      alert(`Erreur : ${err instanceof Error ? err.message : 'Impossible de supprimer le compte'}`)
    } finally {
      setDeletingId(null)
    }
  }

  // Filtrage
  const filtered = accounts.filter((a) => {
    const matchesSearch =
      searchTerm.trim() === '' ||
      a.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.phone?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesRole = filterRole === 'ALL' ? true : a.role === filterRole

    return matchesSearch && matchesRole
  })

  // Tri
  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0
    switch (sortKey) {
      case 'full_name':
        cmp = (a.full_name ?? '').localeCompare(b.full_name ?? '')
        break
      case 'email':
        cmp = (a.email ?? '').localeCompare(b.email ?? '')
        break
      case 'created_at':
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        break
      case 'order_count':
        cmp = a.order_count - b.order_count
        break
      case 'role':
        cmp = (a.role ?? '').localeCompare(b.role ?? '')
        break
    }
    return sortDir === 'asc' ? cmp : -cmp
  })

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  const statusMessage = loading ? 'Chargement des comptes...' : error

  return (
    <div className="card" data-testid="admin-accounts-page">
      <div className="card-header">
        <h1 className="card-title">Gestion des comptes</h1>
        <p style={{ color: '#9ca3af', marginTop: '.25rem' }}>
          {!loading && !error && `${sorted.length} compte${sorted.length > 1 ? 's' : ''} affiché${sorted.length > 1 ? 's' : ''}`}
        </p>
        {statusMessage && (
          <p style={{ color: error ? '#f87171' : '#9ca3af', marginTop: '.25rem' }}>{statusMessage}</p>
        )}
      </div>

      {/* Filtres */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1rem',
          alignItems: 'center',
        }}
      >
        <input
          data-testid="admin-accounts-search"
          type="text"
          placeholder="Rechercher (nom, email, téléphone)"
          className="input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1 1 200px' }}
        />

        <select
          data-testid="admin-accounts-filter-role"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="input"
        >
          <option value="ALL">Tous les rôles</option>
          <option value="USER">USER</option>
          <option value="ADMIN">ADMIN</option>
        </select>

        <button className="btn btn-ghost" data-testid="admin-accounts-refresh" onClick={loadAccounts}>
          🔄 Rafraîchir
        </button>
      </div>

      {/* Tableau */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '.9rem',
          }}
        >
          <thead style={{ background: '#0f172a', color: '#f8fafc' }}>
            <tr>
              <th style={{ padding: '.5rem' }}>#</th>
              <th
                style={{ padding: '.5rem', cursor: 'pointer' }}
                onClick={() => handleSort('full_name')}
              >
                Nom{sortIndicator('full_name')}
              </th>
              <th
                style={{ padding: '.5rem', cursor: 'pointer' }}
                onClick={() => handleSort('email')}
              >
                Email{sortIndicator('email')}
              </th>
              <th style={{ padding: '.5rem' }}>Téléphone</th>
              <th
                style={{ padding: '.5rem', cursor: 'pointer' }}
                onClick={() => handleSort('role')}
              >
                Rôle{sortIndicator('role')}
              </th>
              <th style={{ padding: '.5rem' }}>Ville</th>
              <th style={{ padding: '.5rem' }}>Partenaire</th>
              <th
                style={{ padding: '.5rem', cursor: 'pointer' }}
                onClick={() => handleSort('order_count')}
              >
                Réservations{sortIndicator('order_count')}
              </th>
              <th
                style={{ padding: '.5rem', cursor: 'pointer' }}
                onClick={() => handleSort('created_at')}
              >
                Créé le{sortIndicator('created_at')}
              </th>
              <th style={{ padding: '.5rem' }}>Modifié le</th>
              <th style={{ padding: '.5rem' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((a, index) => (
              <tr
                key={a.id}
                data-testid={`admin-account-row-${a.id}`}
                style={{
                  borderBottom: '1px solid rgba(148,163,184,0.2)',
                  background: a.role === 'ADMIN' ? 'rgba(59,130,246,0.06)' : 'transparent',
                }}
              >
                <td style={{ padding: '.5rem' }}>{index + 1}</td>
                <td style={{ padding: '.5rem' }}>{a.full_name ?? '—'}</td>
                <td style={{ padding: '.5rem' }}>{a.email ?? '—'}</td>
                <td style={{ padding: '.5rem' }}>{a.phone ?? '—'}</td>
                <td style={{ padding: '.5rem' }}>
                  <span
                    style={{
                      fontSize: '.75rem',
                      fontWeight: 600,
                      padding: '.15rem .4rem',
                      borderRadius: '4px',
                      background: a.role === 'ADMIN' ? '#3b82f6' : '#334155',
                      color: '#f8fafc',
                    }}
                  >
                    {a.role ?? '—'}
                  </span>
                </td>
                <td style={{ padding: '.5rem' }}>
                  {[a.city, a.zip_code].filter(Boolean).join(' ') || '—'}
                </td>
                <td style={{ padding: '.5rem' }}>
                  {a.partner_role ? (
                    <span
                      style={{
                        fontSize: '.75rem',
                        fontWeight: 600,
                        padding: '.15rem .4rem',
                        borderRadius: '4px',
                        background: '#8b5cf6',
                        color: '#f8fafc',
                      }}
                    >
                      {a.partner_role}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ padding: '.5rem', textAlign: 'center' }}>
                  <span
                    style={{
                      fontWeight: 600,
                      color: a.order_count > 0 ? '#22c55e' : '#64748b',
                    }}
                  >
                    {a.order_count}
                  </span>
                </td>
                <td style={{ padding: '.5rem' }}>
                  {new Date(a.created_at).toLocaleDateString('fr-CH')}
                </td>
                <td style={{ padding: '.5rem' }}>
                  {new Date(a.updated_at).toLocaleDateString('fr-CH')}
                </td>
                <td style={{ padding: '.5rem' }}>
                  <button
                    data-testid={`admin-account-delete-${a.id}`}
                    className="btn btn-ghost"
                    style={{
                      fontSize: '.8rem',
                      padding: '.2rem .5rem',
                      color: '#f87171',
                      cursor: 'pointer',
                    }}
                    disabled={deletingId === a.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(a)
                    }}
                  >
                    {deletingId === a.id ? '⏳' : '🗑️ Supprimer'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && !loading && (
        <p style={{ color: '#9ca3af', marginTop: '1rem' }}>
          Aucun compte ne correspond à ta recherche.
        </p>
      )}
    </div>
  )
}

export default AdminAccountsPage
